import { apiClient } from '../apiClient';

/**
 * Helper to extract company slug from current URL path
 * CRITICAL: Returns empty string if company not found to prevent malformed URLs
 */
const getCompanySlug = (): string => {
  if (typeof window === 'undefined') return '';
  const parts = window.location.pathname.split('/').filter(Boolean);
  const company = parts[0] || '';
  
  // CRITICAL FIX: Validate company slug to prevent empty/malformed URLs
  if (!company || company === 'undefined' || company.trim() === '') {
    console.warn('[rpcSearch] Company slug is empty or invalid:', {
      pathname: window.location.pathname,
      parts,
    });
    return '';
  }
  
  return company;
};

/**
 * Build RPC endpoint URL
 * CRITICAL: Validates company before building URL to prevent 404s
 */
const buildUrl = (path: string): string => {
  const company = getCompanySlug();
  
  // CRITICAL FIX: Prevent API calls with empty company (would cause 404)
  if (!company) {
    const error = new Error('Company slug is required but not available. Please ensure you are on a valid company page.');
    console.error('[rpcSearch] buildUrl failed:', error.message, {
      pathname: window.location.pathname,
    });
    throw error; // Throw error to prevent malformed request
  }
  
  return `/api/${company}/rpc/search${path}`;
};

/**
 * Search result types
 */
export interface SearchResult {
  type: 'clients' | 'leads' | 'tasks' | 'orders' | 'inventory' | 'users' | 'proposals';
  id: string;
  title: string;
  subtitle: string;
  meta: {
    phone?: string;
    email?: string;
    status?: string;
    publicId?: string;
    leadNum?: string;
    orderNumber?: string;
    sku?: string;
    quantity?: number;
    role?: string;
    dueAt?: string;
    currency?: string;
    amount?: number;
  };
}

export interface GlobalSearchResponse {
  ok: boolean;
  q: string;
  results: SearchResult[];
  grouped: {
    clients: SearchResult[];
    leads: SearchResult[];
    tasks: SearchResult[];
    orders: SearchResult[];
    inventory: SearchResult[];
    users: SearchResult[];
    proposals: SearchResult[];
  };
}

/**
 * RPC client for global search operations
 * All endpoints return { ok: true, data } or { ok: false, error }
 */
const rpcSearch = {
  /**
   * Global search across all entities
   * @param q - Search query string
   * @param limit - Results per entity type (default: 5)
   * @param types - Entity types to search (default: all)
   */
  async globalSearch(
    q: string,
    limit: number = 5,
    types: Array<'clients' | 'leads' | 'tasks' | 'orders' | 'inventory' | 'users' | 'proposals'> = [
      'clients',
      'leads',
      'tasks',
      'orders',
      'inventory',
      'users',
      'proposals',
    ]
  ): Promise<GlobalSearchResponse> {
    const { data } = await apiClient.post<GlobalSearchResponse>(buildUrl('/global'), {
      q,
      limit,
      types,
    });
    
    // Handle both { ok: true, ... } and direct response formats
    if (data?.ok) {
      return data;
    }
    
    // If response doesn't have ok flag, assume it's the data directly
    return {
      ok: true,
      q: q,
      results: Array.isArray(data?.results) ? data.results : [],
      grouped: data?.grouped || {
        clients: [],
        leads: [],
        tasks: [],
        orders: [],
        inventory: [],
        users: [],
        proposals: [],
      },
    };
  },
};

export default rpcSearch;

